import { ComponentType, CSSProperties, memo, useEffect, useRef } from 'react';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { map } from 'rxjs';
import { ViewIds } from '@ardoq/api-types';
import { ErrorBoundary } from '@ardoq/error-boundary';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  MAIN_PANE_LEFT,
  MetaInfoViewStatus,
  PaneLocation,
} from 'streams/views/mainContent/types';
import { ViewContainer } from 'tabview/ViewContainer';
import { DiscontinuedViewReplacement } from 'viewDeprecation/DiscontinuedViewReplacement';
import { DisabledViewReplacement } from 'tabview/DisabledViewReplacement';
import { VisualizationsErrorMessage } from 'tabview/VisualizationErrorMessage';
import { logVisualizationError } from 'tabview/utils';
import { ViewSidePanel } from 'views/mainApp/viewSidePanel/ViewSidePanel';
import {
  LeftPaneSettingsBar,
  RightPaneSettingsBar,
} from './viewPaneSettingsBar/ViewPaneSettingsBar';
import { hasViewPaneSettingsBar } from './viewPaneSettingsBar/viewPaneSettingsBar$';
import { ViewPaneTabBar as TabBar } from './TabBar';
import { SettingsBarBottomSlot } from '@ardoq/settings-bar';
import { WorkspaceModeSlidesEndBanner } from './WorkspaceModeSlidesEndBanner';
import { leftPaneCreated, rightPaneCreated } from './actions';
import { ViewPaneContainerElement } from './viewPaneContainerAtoms';
import { MainViewChrome } from './MainViewChrome';
import { getWorkspaceModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { toWorkspaceModeMainViewInstanceId } from './mainViewInstanceIds';
import { MainViewContent } from './types';
import {
  leftWorkspaceModeViewPaneTab$,
  rightWorkspaceModeViewPaneTab$,
  WorkspaceModeViewPaneTab,
} from './workspaceModeViewPaneTabs$';
import { Features, hasFeature } from '@ardoq/features';
import { ConnectedPagesView as PagesView } from 'tabview/pagesView/PagesView';
import { ConnectedTagscapeView as TagscapeView } from 'tabview/tagscape/tagscapeView';
import { ConnectedCapabilityMap as CapabilityMap } from 'tabview/capabilityMap/CapabilityMapView';
import { ComponentTableView } from 'tabview/tableViews/ComponentTableView';
import { ReferenceTableView } from 'tabview/tableViews/ReferenceTableView';
import { ConnectedLegacyRoadmapView } from 'tabview/roadMap/roadmapView';
import { RoadmapViewContainer as RoadmapView2026 } from 'tabview/roadmap2026/RoadmapViewContainer';
import { ConnectedSwimlaneView as SwimlaneView } from 'tabview/swimlaneView/SwimlaneView';
import { ConnectedComponentMatrix as ComponentMatrixView } from 'tabview/componentMatrix/ComponentMatrixView';
import { ConnectedBlockDiagramView as BlockDiagramView } from 'tabview/blockDiagram/BlockDiagramView';
import { ConnectedRelationshipsView as RelationshipsView } from 'tabview/relationsD3View/RelationshipsView';
import { WorkspaceModeRelationshipsView } from 'tabview/relationsD3View/hierarchical/WorkspaceModeRelationshipsView';
import { ConnectedIsometricBlockDiagramView as IsometricBlockDiagramView } from 'tabview/blockDiagram/IsometricBlockDiagramView';
import { WorkspaceModeDependencyMapView } from 'tabview/dependencyMap/workspaceMode/WorkspaceModeDependencyMapView';
import { WorkspaceModeTimelineView } from 'tabview/timeline2024/WorkspaceModeTimelineView';
import { WorkspaceModeBubbleChart } from 'tabview/bubbleChart/WorkspaceModeBubbleChart';
import {
  ComponentTreeView,
  DependencyMatrixView,
  DependencyWheelView,
  SpiderChartView,
  TreemapView,
} from 'tabview';
import type {
  DiscontinuedViewKey,
  WorkspaceModeViewKey,
} from 'views/environmentals/viewSettings/types';
import type { WorkspaceModeViewProps } from 'tabview/types';

type RenderedWorkspaceModeViewKey = Exclude<
  WorkspaceModeViewKey,
  DiscontinuedViewKey
>;

type WorkspaceModeViewComponents = {
  [ViewId in RenderedWorkspaceModeViewKey]: ComponentType<
    WorkspaceModeViewProps<ViewId>
  >;
};

const getWorkspaceModeViewComponents = (): WorkspaceModeViewComponents => ({
  [ViewIds.PAGESVIEW]: PagesView,
  [ViewIds.TAGSCAPE]: TagscapeView,
  [ViewIds.TABLEVIEW]: ComponentTableView,
  [ViewIds.COMPONENTTREE]: ComponentTreeView,
  [ViewIds.DEPWHEEL]: DependencyWheelView,
  [ViewIds.TREEMAP]: TreemapView,
  [ViewIds.BLOCK_DIAGRAM]: BlockDiagramView,
  [ViewIds.SWIMLANE]: SwimlaneView,
  [ViewIds.DEPMATRIX]: DependencyMatrixView,
  [ViewIds.ROADMAP]: hasFeature(Features.ROADMAP_2026)
    ? RoadmapView2026
    : ConnectedLegacyRoadmapView,
  [ViewIds.DEPENDENCY_MAP_2]: WorkspaceModeDependencyMapView,
  [ViewIds.SPIDER]: SpiderChartView,
  [ViewIds.REFERENCETABLE]: ReferenceTableView,
  [ViewIds.CAPABILITY_MAP]: CapabilityMap,
  [ViewIds.BUBBLE]: WorkspaceModeBubbleChart,
  [ViewIds.TIMELINE]: WorkspaceModeTimelineView,
  [ViewIds.COMPONENT_MATRIX]: ComponentMatrixView,
  [ViewIds.NETWORK]: RelationshipsView,
  [ViewIds.ISOMETRIC_BLOCK_DIAGRAM]: IsometricBlockDiagramView,
  [ViewIds.RELATIONSHIPS_3]: WorkspaceModeRelationshipsView,
});

const settingsBarBottomSlot = <WorkspaceModeSlidesEndBanner />;

const isRenderedWorkspaceModeView = (
  viewId: WorkspaceModeViewKey
): viewId is RenderedWorkspaceModeViewKey =>
  Object.hasOwn(getWorkspaceModeViewComponents(), viewId);

type WorkspaceModeMainViewContent = MainViewContent<
  WorkspaceModeViewProps<RenderedWorkspaceModeViewKey>
>;

type WorkspaceModeMainViewProps<ViewId extends RenderedWorkspaceModeViewKey> = {
  location: PaneLocation;
  activeTabId: WorkspaceModeViewKey | ViewIds.NONE;
  content: MainViewContent<WorkspaceModeViewProps<ViewId>>;
};

const resolveActiveView = ({
  location,
  activeTabId,
  metaInfo,
}: WorkspaceModeViewPaneTab): WorkspaceModeMainViewContent => {
  if (activeTabId === ViewIds.NONE) {
    return { kind: 'none' };
  }
  if (metaInfo?.viewStatus === MetaInfoViewStatus.DISCONTINUED) {
    return { kind: 'discontinued' };
  }
  if (!metaInfo || !isRenderedWorkspaceModeView(activeTabId)) {
    return { kind: 'disabled' };
  }
  const viewInstanceId = toWorkspaceModeMainViewInstanceId(
    location,
    activeTabId
  );
  return {
    kind: 'view',
    viewProps: {
      viewId: activeTabId,
      viewInstanceId,
      isViewpointMode: false,
      viewSettingsWithChanges$: getWorkspaceModeViewSettingsWithChanges$(
        activeTabId,
        viewInstanceId
      ),
    },
  };
};

const toMainViewProps$ = (viewPaneTab$: typeof leftWorkspaceModeViewPaneTab$) =>
  viewPaneTab$.pipe(
    map(viewPaneTab => ({
      location: viewPaneTab.location,
      activeTabId: viewPaneTab.activeTabId,
      content: resolveActiveView(viewPaneTab),
    }))
  );

const leftWorkspaceModeMainViewProps$ = toMainViewProps$(
  leftWorkspaceModeViewPaneTab$
);
const rightWorkspaceModeMainViewProps$ = toMainViewProps$(
  rightWorkspaceModeViewPaneTab$
);

const WorkspaceModeMainView = memo(
  <ViewId extends RenderedWorkspaceModeViewKey>({
    location,
    activeTabId,
    content,
  }: WorkspaceModeMainViewProps<ViewId>) => {
    const ViewComponent: ComponentType<WorkspaceModeViewProps<ViewId>> | null =
      content.kind === 'view'
        ? getWorkspaceModeViewComponents()[content.viewProps.viewId]
        : null;
    const hasSettingsBar =
      content.kind === 'view' && hasViewPaneSettingsBar(activeTabId);
    return (
      <SettingsBarBottomSlot.Provider value={settingsBarBottomSlot}>
        <MainViewChrome />
        {hasSettingsBar &&
          (location === MAIN_PANE_LEFT ? (
            <LeftPaneSettingsBar />
          ) : (
            <RightPaneSettingsBar />
          ))}

        <ErrorBoundary
          logError={() =>
            logVisualizationError({
              viewId: activeTabId,
              location,
              isViewpointMode: false,
            })
          }
          errorMessage={<VisualizationsErrorMessage />}
          resetKeys={[activeTabId]}
        >
          {content.kind === 'view' && ViewComponent && (
            <ViewComponent
              key={content.viewProps.viewInstanceId}
              {...content.viewProps}
            />
          )}
          {content.kind === 'disabled' && <DisabledViewReplacement />}
          {content.kind === 'discontinued' && <DiscontinuedViewReplacement />}
        </ErrorBoundary>
      </SettingsBarBottomSlot.Provider>
    );
  }
);

const LeftMainPane = connect(
  WorkspaceModeMainView,
  leftWorkspaceModeMainViewProps$
);
const RightMainPane = connect(
  WorkspaceModeMainView,
  rightWorkspaceModeMainViewProps$
);

export const ConnectedWorkspaceModeMainView = ({
  location,
}: {
  location: PaneLocation;
}) => (location === MAIN_PANE_LEFT ? <LeftMainPane /> : <RightMainPane />);

type WorkspaceModeMainViewPaneProps = {
  style: CSSProperties;
  location: PaneLocation;
  isSplitPane: boolean;
  isRightEditorPaneOpen: boolean;
  i18nLocale: I18nLocale;
};

export const WorkspaceModeMainViewPane = memo(
  ({
    style,
    location,
    isSplitPane,
    isRightEditorPaneOpen,
    i18nLocale,
  }: WorkspaceModeMainViewPaneProps) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const action =
        location === MAIN_PANE_LEFT ? leftPaneCreated : rightPaneCreated;
      dispatchAction(action(ref));
      return () => dispatchAction(action(null));
    }, [location]);

    return (
      <ViewPaneContainerElement
        style={style}
        data-test-id={location}
        role="region"
        aria-label={
          location === MAIN_PANE_LEFT
            ? t(i18nLocale, 'appLayout:viewPaneAriaLabel', 'View pane')
            : t(
                i18nLocale,
                'appLayout:rightViewPaneAriaLabel',
                'Right view pane'
              )
        }
      >
        <TabBar
          location={location}
          isSplitPane={isSplitPane}
          isRightEditorPaneOpen={isRightEditorPaneOpen}
        />
        <ViewContainer ref={ref}>
          <ConnectedWorkspaceModeMainView location={location} />
        </ViewContainer>
        <ViewSidePanel />
      </ViewPaneContainerElement>
    );
  }
);
